import { ACCESS } from '@/features/access/access';
import { useRoleAccess } from '@/features/access/use-role-access';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Skeleton } from '../ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { getAvatarInitial } from '@/lib/avatar-color';
import { getErrorMessage } from '@/lib/errors';
import { signOut } from '@/lib/auth';
import { useAuthStore } from '@/lib/stores/auth';
import { toast } from '@/toast/useToast';
import {
  useWorkspacesQuery,
  useSwitchWorkspaceMutation,
} from '@/features/settings/queries';
import { ChevronsUpDown, Plus } from '@/icons/Icon';

type Props = {
  collapsed?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Persistent workspace switcher pinned to the top of the Sidebar. Reads the
 * active workspace from the auth store (never `useCurrentWorkspaceId`, which
 * reads `useSearch({ from: '/settings' })` and throws off the settings route —
 * see `Sidebar.tsx`), lists every visible workspace (initial tile, name, and
 * daily warmup budget), and switches via `useSwitchWorkspaceMutation` (which
 * rotates the token and invalidates the query cache atomically). "New workspace"
 * defers to the existing `NewWorkspaceDialog` on `/settings`.
 *
 * TRU-987: because this renders on every authenticated route (via
 * `AppShell`/`Sidebar`), it's also the one place that always has both the
 * active workspace and the caller's membership-scoped workspace list at
 * once — so it doubles as the app-wide guard for "my active workspace was
 * deleted, or I was removed as a member of it, while my tab sat open".
 * When the active id drops out of the fetched list, auto-switch to the
 * first workspace still visible, same as a manual click.
 */
export function WorkspaceSwitcher({ collapsed = false, className, style }: Props) {
  const navigate = useNavigate();

  // Controlled so `handleSwitch` can close the menu on settle (TRU-1247 bug 1).
  // The row keeps its `preventDefault()` so the in-row "Switching…" state has
  // time to paint — the default close would unmount it first.
  const [open, setOpen] = useState(false);

  const current = useAuthStore((s) => s.workspace);
  const workspacesQuery = useWorkspacesQuery();
  const switchWorkspaceMutation = useSwitchWorkspaceMutation();
  const { allowed: canCreateWorkspace } = useRoleAccess(ACCESS.workspaceCreate);

  const workspaces = workspacesQuery.data ?? [];

  const isStale =
    workspacesQuery.isSuccess && current != null && !workspaces.some((w) => w.id === current.id);
  // ponytail: at most one in-flight attempt per stale id — a failed attempt
  // un-marks itself (below) so a later `workspaces` refetch can retry, but
  // nothing here busy-retries between refetches.
  const attemptedStaleIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isStale) {
      attemptedStaleIdRef.current = null;
      return;
    }
    if (attemptedStaleIdRef.current === current!.id) return;
    const fallback = workspaces[0];
    if (!fallback) return;
    attemptedStaleIdRef.current = current!.id;
    void switchWorkspaceMutation.mutateAsync(fallback.id).catch(() => {
      // Transient failure (network blip, 500): un-mark so the next
      // `workspaces` refetch (window focus, 30s staleTime) gets another
      // attempt instead of giving up on this id forever.
      attemptedStaleIdRef.current = null;
    });
  }, [isStale, current?.id, workspaces]);

  // TRU-985 (Case 2): the caller's membership-scoped workspace list came back
  // empty — they were removed from their only (or last remaining) workspace.
  // Unlike the stale-id branch above, there is no fallback to switch to, so
  // the isStale effect's `if (!fallback) return` is a permanent no-op here
  // and the sidebar/Team tab would otherwise keep showing the now-gone
  // workspace forever. Sign out entirely instead — `signOut()` clears the
  // Zustand auth store (not just storage keys), which the route guards need
  // to avoid a stale-token redirect loop (see apps/web/CLAUDE.md → Route
  // guards).
  const isEmpty = workspacesQuery.isSuccess && workspaces.length === 0;
  const signedOutRef = useRef(false);
  useEffect(() => {
    if (!isEmpty || signedOutRef.current) return;
    signedOutRef.current = true;
    void signOut().finally(() => {
      void navigate({ to: '/login', replace: true });
    });
  }, [isEmpty, navigate]);

  const currentName = current?.name ?? 'Workspace';
  // Falls back to "Workspace" (→ "W") for an empty name, matching the prior
  // hand-rolled default rather than the shared helper's "?".
  const initial = getAvatarInitial(current?.name || 'Workspace');

  async function handleSwitch(id: number) {
    if (id === current?.id) return;
    try {
      // The mutation's onSuccess rotates the token (setAuth) and invalidates
      // the cache atomically, so the post-switch refetches carry the new
      // workspace's JWT. Don't re-store here — that would race the refetch.
      await switchWorkspaceMutation.mutateAsync(id);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to switch workspace'));
    } finally {
      // Close on settle either way — a failure already surfaced as a toast, so
      // there's nothing left for the row to show.
      setOpen(false);
    }
  }

  // Expanded: a flat row inside the sidebar's workspace card (the card owns
  // the border). Collapsed: a 38px dark tile on the rail.
  const trigger = collapsed ? (
    <button
      type="button"
      aria-label={`Switch workspace · ${currentName}`}
      className="sidebar-ws-tile"
    >
      {initial}
    </button>
  ) : (
    <button type="button" className="sidebar-ws-trigger">
      <span className="sidebar-ws-mark" aria-hidden>
        {initial}
      </span>
      <span className="sidebar-ws-name truncate" title={currentName}>
        {currentName}
      </span>
      <span className="inline-flex shrink-0 text-[color:var(--ink-400)]" aria-hidden>
        <ChevronsUpDown size={13} />
      </span>
    </button>
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {collapsed ? (
        // Both `asChild` layers must land on the same real <button>. The old
        // shape wrapped Tooltip.Root — a context provider that forwards no DOM
        // props — so onClick/aria-expanded were dropped (TRU-1247 bug 2).
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10}>
            {currentName}
          </TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent
        side={collapsed ? 'right' : 'bottom'}
        align="start"
        sideOffset={collapsed ? 8 : 4}
        aria-label="Switch workspace"
        // Expanded, the menu drops over the card at the card's own width (the
        // trigger spans it); on the rail it opens beside the tile instead.
        className={cn(
          'sidebar-ws-menu',
          // `max-w` stays as a ceiling (TRU-990): a long name truncates in its
          // row instead of growing the panel off the page.
          'max-w-[280px]',
          collapsed ? 'w-[236px]' : 'w-[var(--radix-dropdown-menu-trigger-width)]',
          className,
        )}
        style={style}
      >
        {workspacesQuery.isPending ? (
          <div className="flex flex-col gap-1 p-1">
            <Skeleton className="h-[26px] w-full" />
            <Skeleton className="h-[26px] w-full" />
          </div>
        ) : workspacesQuery.isError ? (
          <DropdownMenuItem
            className="sidebar-ws-option"
            onSelect={(e) => {
              e.preventDefault();
              void workspacesQuery.refetch();
            }}
          >
            Couldn&apos;t load workspaces · Retry
          </DropdownMenuItem>
        ) : (
          workspaces.map((w) => {
            const isActive = w.id === current?.id;
            const isSwitching =
              switchWorkspaceMutation.isPending && switchWorkspaceMutation.variables === w.id;
            const budget = w.quota?.dailyWarmupBudget;
            return (
              <DropdownMenuItem
                key={w.id}
                aria-current={isActive ? 'true' : undefined}
                disabled={isSwitching}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!isActive) void handleSwitch(w.id);
                }}
                className={cn('sidebar-ws-option', isActive && 'is-active')}
              >
                <span className="sidebar-ws-option-mark" aria-hidden>
                  {getAvatarInitial(w.name || 'Workspace')}
                </span>
                <span className="min-w-0 flex-1 truncate" title={w.name}>
                  {w.name}
                </span>
                {isSwitching ? (
                  <span className="sidebar-ws-option-meta">Switching…</span>
                ) : budget != null ? (
                  <span className="sidebar-ws-option-meta num">{`${budget}/day`}</span>
                ) : null}
              </DropdownMenuItem>
            );
          })
        )}

        {canCreateWorkspace && (
          <>
            <DropdownMenuSeparator className="sidebar-ws-menu-sep" />
            <DropdownMenuItem
              className="sidebar-ws-new"
              onSelect={() => {
                void navigate({
                  to: '/settings',
                  search: { section: 'workspaces', dialog: 'new-workspace' },
                });
              }}
            >
              <Plus size={14} />
              New workspace
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
